import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { OracleProvider } from "@/contexts/OracleContext"; 
import Notifications from "@/pages/Notifications";

// Layout
import { Layout } from "@/components/layout/Layout";
import OfflineOverlay from "@/components/layout/OfflineOverlay";

// Pages
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import Onboarding from "@/pages/Onboarding";
import Home from "@/pages/Home";
import Transactions from "@/pages/Transactions";
import Goals from "@/pages/Goals";
import Profile from "@/pages/Profile";
import ProfilePersonal from "@/pages/ProfilePersonal";
import ProfilePayment from "@/pages/ProfilePayment";
import ProfileFeedback from "@/pages/ProfileFeedback";
import PWAInstall from "@/pages/PWAInstall";
import PaymentChecklist from "@/pages/PaymentChecklist";
import Connections from "@/pages/Connections";
import Reports from "@/pages/Reports";
import PenteFino from "@/pages/PenteFino";
import Legal from "./pages/Legal";

// Loading component
function LoadingScreen() {
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="text-center">
        <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-violet-600 rounded-3xl flex items-center justify-center mx-auto mb-4 animate-pulse">
          <span className="text-3xl">🐷</span>
        </div>
        <p className="text-slate-600 font-medium">Carregando...</p>
      </div>
    </div>
  );
}

// Protected Route wrapper
function ProtectedRoute({ children }) {
  const { user, loading, isEmailVerified, hasCompletedOnboarding } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const isVerifyPage = location.pathname === "/verify-email";
  const isOnboardingPage = location.pathname === "/onboarding";

  if (!isEmailVerified && !isVerifyPage) {
    return <Navigate to="/verify-email" replace />;
  }

  if (isEmailVerified && !hasCompletedOnboarding && !isOnboardingPage && !isVerifyPage) {
    return <Navigate to="/onboarding" replace />;
  }

  return children;
}

// Public Route wrapper
function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (user) {
    return <Navigate to="/home" replace />;
  }

  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<PublicRoute><Landing /></PublicRoute>} />
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
      <Route path="/reset-password" element={<PublicRoute><ResetPassword /></PublicRoute>} />
      <Route path="/legal" element={<Legal />} /> 
      
      {/* Semi-protected */}
      <Route path="/verify-email" element={<ProtectedRoute><VerifyEmail /></ProtectedRoute>} />
      <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
      
      {/* Protected Routes with Layout */}
      <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route path="/home" element={<Home />} />
        <Route path="/transactions" element={<Transactions />} />
        <Route path="/goals" element={<Goals />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/profile/personal" element={<ProfilePersonal />} />
        <Route path="/profile/payment" element={<ProfilePayment />} />
        <Route path="/profile/feedback" element={<ProfileFeedback />} />
        <Route path="/profile/pwa-install" element={<PWAInstall />} /> { /* <--- ADICIONE ESTA LINHA */ }
        <Route path="/payment-checklist" element={<PaymentChecklist />} />
        <Route path="/connections" element={<Connections />} />
        <Route path="/reports" element={<Reports />} />
         <Route path="/pente-fino" element={<PenteFino />} />
        <Route path="/notifications" element={<Notifications />} />
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// FUNÇÃO ÚNICA DO APP NO FINAL
function App() {
  return (
    <div className="App">
      <AuthProvider>
        <OracleProvider>
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
          
          <Toaster 
            position="top-center"
            toastOptions={{
              className: "rounded-2xl",
            }}
          />
        </OracleProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
